import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import moment from "moment";
import {
  Bot, UserRound, Search, Send, Phone, CalendarPlus, Hand, RotateCcw, AlertTriangle, Smartphone, X, List,
  BookOpen, Plus, Trash2, CheckCheck, FileText, Sparkles, MessageCircle,
} from "lucide-react";
import SegmentedControl from "../../components/SegmentedControl";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { cn } from "../../lib/utils";
import { generateDemoChats, botReply, DEFAULT_KB, DOCTORS, STAFF, CLINIC, dayLabel } from "../../lib/demo-whatsapp";

// Demo-only (see navigations/main.js): what MediPro's WhatsApp inbox with an
// AI-first assistant would look like. Conversations and the "AI" are canned
// (lib/demo-whatsapp.js); the simulator lets anyone play the patient.

const SIM_ID = "sim";
const ME = "You";

const PRIORITY_STYLE = {
  urgent: "bg-destructive text-destructive-foreground",
  high: "bg-warning text-warning-foreground",
  normal: "bg-secondary text-secondary-foreground",
};

const lastOf = (chat) => chat.messages.filter((m) => m.from !== "event").at(-1);

// ------------------------------------------------------------ message bubble

function Bubble({ m, onTap, phone }) {
  if (m.from === "event") {
    return (
      <div className="my-2 flex justify-center">
        <span className="rounded-full bg-muted px-3 py-1 text-center text-[11px] text-muted-foreground">
          {m.text} · {moment(m.at).format("h:mm A")}
        </span>
      </div>
    );
  }
  const mine = phone ? m.from === "patient" : m.from !== "patient";
  const interactive = phone && !mine && onTap;
  return (
    <div className={cn("my-1.5 flex", mine ? "justify-end" : "justify-start")}>
      <div className={cn("max-w-[82%] space-y-1")}>
        {!phone && m.from !== "patient" && (
          <p className={cn("flex items-center gap-1 text-[10px] font-medium", mine ? "justify-end" : "")}>
            {m.from === "ai" ? <><Bot className="h-3 w-3" /> AI assistant{m.template ? " · template" : ""}</> : <><UserRound className="h-3 w-3" /> {m.by}</>}
          </p>
        )}
        <div
          className={cn(
            "whitespace-pre-line rounded-lg px-3 py-2 text-sm shadow-sm",
            mine ? "rounded-tr-none bg-[#d9fdd3] text-[#111b21]" : "rounded-tl-none bg-white text-[#111b21]",
            !phone && m.from === "staff" && "bg-[#dbeafe]",
            m.tapped && "italic"
          )}
        >
          {m.document ? (
            <span className="flex items-center gap-2"><FileText className="h-4 w-4 text-red-600" /> {m.text.replace("📄 ", "")}</span>
          ) : (
            m.text
          )}
          <span className="ml-2 inline-flex items-center gap-0.5 align-bottom text-[10px] text-[#667781]">
            {moment(m.at).format("h:mm A")}
            {mine && <CheckCheck className="h-3 w-3 text-[#53bdeb]" />}
          </span>
        </div>
        {m.buttons && (
          <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${Math.min(m.buttons.length, 3)}, minmax(0, 1fr))` }}>
            {m.buttons.map((b) => (
              <button
                key={b.id}
                type="button"
                disabled={!interactive}
                onClick={() => onTap(b.id, b.title)}
                className={cn("rounded-lg bg-white px-2 py-1.5 text-center text-sm font-medium text-[#00a884] shadow-sm", interactive ? "hover:bg-[#f5f6f6]" : "cursor-default opacity-80")}
              >
                {b.title}
              </button>
            ))}
          </div>
        )}
        {m.list && <ListPicker list={m.list} interactive={interactive} onTap={onTap} />}
      </div>
    </div>
  );
}

function ListPicker({ list, interactive, onTap }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-white px-2 py-1.5 text-sm font-medium text-[#00a884] shadow-sm hover:bg-[#f5f6f6]"
      >
        <List className="h-3.5 w-3.5" /> {list.button}
      </button>
      {open && (
        <div className="mt-1 overflow-hidden rounded-lg bg-white shadow-md">
          {list.rows.map((r) => (
            <button
              key={r.id}
              type="button"
              disabled={!interactive}
              onClick={() => { setOpen(false); onTap(String(r.id), r.title); }}
              className={cn("block w-full border-b border-[#f0f2f5] px-3 py-2 text-left last:border-0", interactive ? "hover:bg-[#f5f6f6]" : "cursor-default")}
            >
              <span className="block text-sm text-[#111b21]">{r.title}</span>
              {r.description && <span className="block text-xs text-[#667781]">{r.description}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------- simulator

function PhoneSimulator({ chat, onSend, onClose, typing }) {
  const [text, setText] = useState("");
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chat.messages.length, typing]);

  const send = (e) => {
    e?.preventDefault();
    if (!text.trim()) return;
    onSend(text.trim(), text.trim());
    setText("");
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 flex h-[640px] max-h-[calc(100vh-2rem)] w-[340px] flex-col overflow-hidden rounded-[2rem] border-[6px] border-[#111b21] bg-[#efeae2] shadow-2xl">
      <div className="flex items-center gap-2 bg-[#008069] px-3 py-2.5 text-white">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-xs font-bold">AC</span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{CLINIC}</p>
          <p className="text-[11px] opacity-80">{typing ? "typing…" : "Business account"}</p>
        </div>
        <button onClick={onClose} aria-label="Close simulator" className="rounded p-1 hover:bg-white/10"><X className="h-4 w-4" /></button>
      </div>
      <div className="flex-1 overflow-y-auto px-2.5 py-2">
        <p className="mx-auto my-2 w-fit rounded-md bg-[#fff5c4] px-2 py-1 text-center text-[10px] text-[#54656f]">
          Demo simulator — you are the patient. Try “change my slot”, “fees?”, “talk to someone”.
        </p>
        {chat.messages.filter((m) => m.from !== "event").map((m, i) => (
          <Bubble key={i} m={m} phone onTap={(id, title) => onSend(id, title)} />
        ))}
        {typing && <p className="ml-1 w-fit rounded-lg bg-white px-3 py-2 text-xs text-[#667781] shadow-sm">typing…</p>}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="flex items-center gap-1.5 bg-[#f0f2f5] p-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Message"
          className="h-9 flex-1 rounded-full bg-white px-3 text-sm outline-none"
        />
        <button type="submit" className="flex h-9 w-9 items-center justify-center rounded-full bg-[#00a884] text-white" aria-label="Send">
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
}

// ------------------------------------------------------------ knowledge base

function KnowledgeBase({ kb, setKb }) {
  const [draft, setDraft] = useState({ topic: "", keywords: "", answer: "" });
  const add = (e) => {
    e.preventDefault();
    if (!draft.topic.trim() || !draft.answer.trim()) return toast.error("Topic and answer are required.");
    setKb((all) => [
      ...all,
      { id: `k${Date.now()}`, topic: draft.topic.trim(), keywords: draft.keywords.split(",").map((w) => w.trim().toLowerCase()).filter(Boolean), answer: draft.answer.trim() },
    ]);
    setDraft({ topic: "", keywords: "", answer: "" });
    toast.success("Added — the assistant uses it from the next message.");
  };
  const update = (id, patch) => setKb((all) => all.map((k) => (k.id === id ? { ...k, ...patch } : k)));

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
      <div className="space-y-3">
        {kb.map((k) => (
          <div key={k.id} className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-start justify-between gap-3">
              <input
                value={k.topic}
                onChange={(e) => update(k.id, { topic: e.target.value })}
                className="w-full bg-transparent font-medium outline-none"
              />
              <button onClick={() => setKb((all) => all.filter((x) => x.id !== k.id))} className="text-muted-foreground hover:text-destructive" aria-label={`Delete ${k.topic}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <textarea
              value={k.answer}
              onChange={(e) => update(k.id, { answer: e.target.value })}
              rows={2}
              className="mt-2 w-full resize-y rounded-md border border-input bg-background p-2 text-sm"
            />
            <div className="mt-2 flex flex-wrap gap-1">
              {k.keywords.map((w) => <Badge key={w} variant="outline" className="font-normal">{w}</Badge>)}
            </div>
          </div>
        ))}
      </div>
      <form onSubmit={add} className="h-fit space-y-3 rounded-lg border border-border bg-card p-4 lg:sticky lg:top-0">
        <p className="flex items-center gap-2 font-medium"><Plus className="h-4 w-4" /> Teach the assistant</p>
        <Input value={draft.topic} onChange={(e) => setDraft((d) => ({ ...d, topic: e.target.value }))} placeholder="Topic, e.g. Diwali offer" />
        <Input value={draft.keywords} onChange={(e) => setDraft((d) => ({ ...d, keywords: e.target.value }))} placeholder="Words patients use, comma separated" />
        <textarea
          value={draft.answer}
          onChange={(e) => setDraft((d) => ({ ...d, answer: e.target.value }))}
          rows={4}
          placeholder="The answer the assistant should give"
          className="w-full rounded-md border border-input bg-background p-2 text-sm"
        />
        <Button type="submit" className="w-full">Add to knowledge base</Button>
        <p className="text-xs text-muted-foreground">
          In the real version the assistant also reads live doctors, slots, prices and report status from MediPro, and staff can upload PDFs like the price list.
        </p>
      </form>
    </div>
  );
}

// -------------------------------------------------------------------- page

export default function WhatsAppInbox() {
  const navigate = useNavigate();
  const [chats, setChats] = useState(generateDemoChats);
  const [kb, setKb] = useState(DEFAULT_KB);
  const [view, setView] = useState("inbox");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState("c2");
  const [reply, setReply] = useState("");
  const [simOpen, setSimOpen] = useState(false);
  const [typing, setTyping] = useState(false);
  const botState = useRef({});
  const endRef = useRef(null);

  const selected = chats.find((c) => c.id === selectedId);
  const sim = chats.find((c) => c.id === SIM_ID);

  useEffect(() => { endRef.current?.scrollIntoView(); }, [selectedId, selected?.messages.length]);

  const patch = (id, fn) => setChats((all) => all.map((c) => (c.id === id ? fn(c) : c)));
  const push = (id, ...msgs) => patch(id, (c) => ({ ...c, messages: [...c.messages, ...msgs] }));

  const openSimulator = () => {
    if (!sim) {
      const appointment = { doctor: DOCTORS[0], dayOffset: 1, time: "11:00 AM" };
      botState.current = {};
      setChats((all) => [
        {
          id: SIM_ID, name: "You (simulator)", phone: "9000000000", owner: "ai", status: "open", unread: 0, lead: "Appointment booked",
          summary: "Live demo conversation", appointment,
          messages: [{
            from: "ai", template: true, at: new Date().toISOString(),
            text: `Hello! Your appointment is confirmed ✅\n\n👨‍⚕️ ${appointment.doctor.name}\n📅 ${dayLabel(1)}\n🕐 11:00 AM\n📍 ${CLINIC}\n\nPlease arrive 15 minutes early.`,
            buttons: [{ id: "reschedule", title: "Reschedule" }, { id: "cancel", title: "Cancel" }],
          }],
        },
        ...all,
      ]);
    }
    setSelectedId(SIM_ID);
    setSimOpen(true);
  };

  // A patient message in the simulator → the assistant answers unless a
  // person owns the chat.
  const simSend = (value, label) => {
    const chat = chats.find((c) => c.id === SIM_ID);
    push(SIM_ID, { from: "patient", text: label, at: new Date().toISOString(), tapped: value !== label });
    if (chat.owner === "human") {
      patch(SIM_ID, (c) => ({ ...c, unread: (c.unread || 0) + 1 }));
      return;
    }
    setTyping(true);
    setTimeout(() => {
      const out = botReply(value, botState.current, kb, { appointment: chat.appointment });
      botState.current = out.state;
      setTyping(false);
      patch(SIM_ID, (c) => {
        const next = { ...c, messages: [...c.messages, ...out.replies] };
        if (out.booked) {
          next.appointment = { doctor: out.booked.doctor, dayOffset: out.booked.dayOffset, time: out.booked.time };
          next.messages.push({ from: "event", at: new Date().toISOString(), text: `Appointment ${out.booked.isNew ? "booked" : "rescheduled"} by patient on WhatsApp → ${out.booked.doctor.name}, ${dayLabel(out.booked.dayOffset)} ${out.booked.time}` });
          next.summary = out.booked.isNew ? "Booked by themselves on WhatsApp" : "Rescheduled by themselves on WhatsApp";
        }
        if (out.cancelled) {
          next.appointment = null;
          next.messages.push({ from: "event", at: new Date().toISOString(), text: "Appointment cancelled by patient on WhatsApp" });
        }
        if (out.handover) {
          next.owner = "human";
          next.status = "needs_human";
          next.priority = out.handover.priority;
          next.handover = { reason: out.handover.reason, at: new Date().toISOString() };
          next.summary = out.handover.summary;
          next.unread = 1;
          next.messages.push({ from: "event", at: new Date().toISOString(), text: `${out.handover.priority === "urgent" ? "URGENT handover" : "Handed over to staff"} · ${out.handover.reason}` });
        }
        return next;
      });
      if (out.handover) {
        toast(out.handover.priority === "urgent" ? "🚨 Urgent: patient needs a call now" : "🙋 A patient needs a person", { description: out.handover.summary });
      }
    }, 900);
  };

  const takeOver = () => {
    patch(selectedId, (c) => ({
      ...c, owner: "human", status: c.status === "closed" ? "open" : c.status, assignee: ME,
      messages: [...c.messages, { from: "event", at: new Date().toISOString(), text: `${ME} took over from the AI` }],
    }));
  };
  const handBack = () => {
    if (selectedId === SIM_ID) botState.current = {};
    patch(selectedId, (c) => ({
      ...c, owner: "ai", status: "open", priority: undefined, handover: undefined, unread: 0,
      messages: [...c.messages, { from: "event", at: new Date().toISOString(), text: "Handed back to the AI assistant" }],
    }));
  };
  const sendStaff = (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    patch(selectedId, (c) => ({
      ...c, owner: "human", status: c.status === "needs_human" ? "open" : c.status, assignee: c.assignee || ME, unread: 0,
      messages: [...c.messages, { from: "staff", by: ME, text: reply.trim(), at: new Date().toISOString() }],
    }));
    setReply("");
  };

  const counts = useMemo(() => ({
    all: chats.length,
    needs_human: chats.filter((c) => c.status === "needs_human").length,
    ai: chats.filter((c) => c.owner === "ai" && c.status !== "closed").length,
    human: chats.filter((c) => c.owner === "human" && c.status !== "needs_human").length,
    closed: chats.filter((c) => c.status === "closed").length,
  }), [chats]);

  const list = chats
    .filter((c) =>
      filter === "all" ? true
      : filter === "needs_human" ? c.status === "needs_human"
      : filter === "ai" ? c.owner === "ai" && c.status !== "closed"
      : filter === "human" ? c.owner === "human" && c.status !== "needs_human"
      : c.status === "closed"
    )
    .filter((c) => !search || `${c.name} ${c.phone}`.toLowerCase().includes(search.toLowerCase()))
    .sort((x, y) => {
      const rank = (c) => (c.priority === "urgent" ? 0 : c.status === "needs_human" ? 1 : 2);
      return rank(x) - rank(y) || (lastOf(y)?.at || "").localeCompare(lastOf(x)?.at || "");
    });

  const aiHandled = chats.filter((c) => !c.messages.some((m) => m.from === "staff") && c.status !== "needs_human").length;

  return (
    // Leave room for the simulator phone so it never covers the inbox.
    <div className={cn("space-y-4", simOpen && "xl:pr-[356px]")}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 font-serif text-2xl font-semibold">
            WhatsApp Inbox <Badge variant="outline" className="font-sans text-[10px] font-medium uppercase tracking-wide">Demo</Badge>
          </h2>
          <p className="text-sm text-muted-foreground">
            The AI assistant answers first; chats it can't handle come to the team.{" "}
            <span className="font-medium text-foreground">{aiHandled} of {chats.length}</span> handled fully by AI.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <SegmentedControl
            items={[{ value: "inbox", label: "Inbox", count: counts.needs_human || undefined }, { value: "kb", label: "Knowledge base" }]}
            value={view}
            onChange={setView}
          />
          <Button onClick={openSimulator}>
            <Smartphone className="h-4 w-4" /> Try as patient
          </Button>
        </div>
      </div>

      {view === "kb" ? (
        <KnowledgeBase kb={kb} setKb={setKb} />
      ) : (
        <div className="grid h-[calc(100vh-14rem)] min-h-[560px] overflow-hidden rounded-lg border border-border bg-card lg:grid-cols-[320px_1fr_280px]">
          {/* conversation list */}
          <div className="flex min-h-0 flex-col border-r border-border">
            <div className="space-y-2 border-b border-border p-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or phone" className="h-9 pl-9" />
              </div>
              <div className="flex flex-wrap gap-1">
                {[
                  ["all", "All"], ["needs_human", "Needs human"], ["ai", "AI handling"], ["human", "With staff"], ["closed", "Closed"],
                ].map(([k, label]) => (
                  <button
                    key={k}
                    onClick={() => setFilter(k)}
                    className={cn(
                      "rounded-full border px-2.5 py-1 text-xs",
                      filter === k ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-muted",
                      k === "needs_human" && counts.needs_human > 0 && filter !== k && "border-destructive/40 text-destructive"
                    )}
                  >
                    {label} <span className="tabular-nums opacity-70">{counts[k]}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              {list.map((c) => {
                const last = lastOf(c);
                return (
                  <button
                    key={c.id}
                    onClick={() => { setSelectedId(c.id); patch(c.id, (x) => ({ ...x, unread: 0 })); }}
                    className={cn("flex w-full gap-3 border-b border-border px-3 py-3 text-left hover:bg-muted/50", selectedId === c.id && "bg-muted")}
                  >
                    <span className={cn("mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold", c.id === SIM_ID ? "bg-primary text-primary-foreground" : "bg-secondary")}>
                      {c.name.replace(/[^A-Za-z ]/g, "").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "?"}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-medium">{c.name}</span>
                        <span className="shrink-0 text-[11px] text-muted-foreground">{last && moment(last.at).fromNow(true)}</span>
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                        {last?.from === "ai" ? "🤖 " : last?.from === "staff" ? `${last.by}: ` : ""}{last?.text.split("\n")[0]}
                      </span>
                      <span className="mt-1 flex items-center gap-1.5">
                        {c.status === "needs_human" ? (
                          <span className={cn("inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase", PRIORITY_STYLE[c.priority || "normal"])}>
                            {c.priority === "urgent" && <AlertTriangle className="h-3 w-3" />} Needs human
                          </span>
                        ) : c.owner === "ai" ? (
                          <span className="inline-flex items-center gap-1 rounded bg-green-600/10 px-1.5 py-0.5 text-[10px] font-medium text-green-700"><Bot className="h-3 w-3" /> AI</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded bg-blue-600/10 px-1.5 py-0.5 text-[10px] font-medium text-blue-700"><UserRound className="h-3 w-3" /> {c.assignee || "Staff"}</span>
                        )}
                        {c.unread > 0 && <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-[#25d366] px-1 text-[10px] font-semibold text-white">{c.unread}</span>}
                      </span>
                    </span>
                  </button>
                );
              })}
              {list.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">No conversations here.</p>}
            </div>
          </div>

          {/* thread */}
          {selected ? (
            <div className="flex min-h-0 flex-col">
              <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
                <div className="min-w-0">
                  <p className="truncate font-medium">{selected.name}</p>
                  <p className="text-xs text-muted-foreground">+91 {selected.phone}</p>
                </div>
                {selected.owner === "ai" ? (
                  <Button size="sm" variant="outline" onClick={takeOver}><Hand className="h-3.5 w-3.5" /> Take over</Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={handBack}><RotateCcw className="h-3.5 w-3.5" /> Hand back to AI</Button>
                )}
              </div>
              {selected.status === "needs_human" && (
                <div className={cn("flex items-center gap-2 px-4 py-2 text-sm", selected.priority === "urgent" ? "bg-destructive/10 text-destructive" : "bg-warning/10 text-warning")}>
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <span className="min-w-0 flex-1">
                    <b>{selected.handover?.reason}</b> — waiting {moment(selected.handover?.at).fromNow(true)}. {selected.summary}
                  </span>
                </div>
              )}
              <div className="min-h-0 flex-1 overflow-y-auto bg-[#efeae2] px-4 py-3">
                {selected.messages.map((m, i) => <Bubble key={i} m={m} />)}
                <div ref={endRef} />
              </div>
              <form onSubmit={sendStaff} className="flex items-center gap-2 border-t border-border p-3">
                <Input
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder={selected.owner === "ai" ? "Type to reply — this takes the chat over from the AI" : `Reply as ${ME}`}
                  className="h-10"
                />
                <Button type="submit" disabled={!reply.trim()}><Send className="h-4 w-4" /></Button>
              </form>
            </div>
          ) : (
            <div className="flex items-center justify-center text-sm text-muted-foreground">Select a conversation</div>
          )}

          {/* details */}
          {selected && (
            <div className="hidden min-h-0 space-y-4 overflow-y-auto border-l border-border p-4 lg:block">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">AI summary</p>
                <p className="mt-1 flex gap-1.5 text-sm"><Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" /> {selected.summary}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Upcoming appointment</p>
                {selected.appointment ? (
                  <div className="mt-1 rounded-md border border-border p-2.5 text-sm">
                    <p className="font-medium">{selected.appointment.doctor.name}</p>
                    <p className="text-muted-foreground">{selected.appointment.doctor.dept}</p>
                    <p className="mt-1">{dayLabel(selected.appointment.dayOffset)} · {selected.appointment.time}</p>
                  </div>
                ) : (
                  <p className="mt-1 text-sm text-muted-foreground">None</p>
                )}
              </div>
              {selected.lead && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Lead</p>
                  <p className="mt-1 text-sm">{selected.lead}</p>
                </div>
              )}
              <div className="space-y-2">
                <Button className="w-full" variant="outline" onClick={() => toast("Calling via MCube — your phone rings first (demo).")}>
                  <Phone className="h-4 w-4" /> Call patient
                </Button>
                <Button className="w-full" variant="outline" onClick={() => navigate("/opd")}>
                  <CalendarPlus className="h-4 w-4" /> Book appointment
                </Button>
                <Button className="w-full" variant="outline" onClick={() => setView("kb")}>
                  <BookOpen className="h-4 w-4" /> Teach the assistant
                </Button>
              </div>
              <div className="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
                <p className="flex items-center gap-1 font-medium text-foreground"><MessageCircle className="h-3.5 w-3.5" /> Assigned staff</p>
                <select
                  value={selected.assignee || ""}
                  onChange={(e) => patch(selected.id, (c) => ({ ...c, assignee: e.target.value || undefined }))}
                  className="mt-1.5 h-8 w-full rounded-md border border-input bg-card px-2 text-sm text-foreground"
                >
                  <option value="">Unassigned</option>
                  {[ME, ...STAFF].map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      {simOpen && sim && <PhoneSimulator chat={sim} typing={typing} onSend={simSend} onClose={() => setSimOpen(false)} />}
    </div>
  );
}
